import { skip } from 'rxjs';

import { getLongTermToken } from '../../authorisation-utils/sso-v1-utils/endpoints';
import { longTermToken, systemError, loadingView } from '../../system-utils/events-manager';
import { parseURLSearchParams } from '../../system-utils/url-utils/url-params';
import { clearLongTermTokenFromSessionStorage, getLongTermTokenFromSessionStorage, saveLongTermTokenToSessionStorage } from '../../authorisation-utils/sso-v1-utils/long-term-token-session-storage';
import Config from '../../system-utils/config-manager';
import { pushParamsToHistory } from '../../system-utils/url-utils/url-params';

/**
 * Get Short Term token from the URI and exchange it for a Long Term token
 * Fire a System Error event if the token cannot be generated
 */
export const bootstrapHostApp = async () => {
  // Keep session storage updated
  longTermToken.subject.pipe(skip(1)).subscribe(({ token, isValid }) => {
    /* istanbul ignore next */
    if (isValid) {
      saveLongTermTokenToSessionStorage(token);
    } else if (!token) {
      clearLongTermTokenFromSessionStorage();
    } else {
      // When token exists but is not valid it means we know the token is now invalid and are trying to refresh it.
      // The refresh endpoint has been removed since it is not available for the MFE (we are an internal webapp).
      // Keeping this logic as is since it may be required with SSO 2.0 although it leads to 1 line of missing test coverage
    }
  });

  const params = parseURLSearchParams();
  const accessToken = getAndRemoveTokenFromUrlParams(params, Config.get<string>('url_search_params.access_token', 'access_token'));
  const shortAccessToken = getAndRemoveTokenFromUrlParams(params, Config.get<string>('url_search_params.short_access_token', 'short_access_token'));

  let accessTokenToStore = accessToken;

  if (shortAccessToken && !accessTokenToStore) {
    const response = await getLongTermToken(shortAccessToken);
    if (response.status === 200) {
      const accessToken = response.data?.access_token;
      /* istanbul ignore next */
      if (accessToken) {
        accessTokenToStore = accessToken;
      } else {
        /* istanbul ignore next */
        console.error('Could not retrieve token from response');
      }
    }
  }

  if (!accessTokenToStore) {
    accessTokenToStore = getLongTermTokenFromSessionStorage();
  }

  if (accessTokenToStore) {
    longTermToken.publishValidToken(accessTokenToStore);
  } else {
    longTermToken.clearToken();
    systemError.publishInvalidShortTermToken();
  }
  // TODO: verify loading view event to tests for this case
  loadingView.hideLoadingView();
};

const getAndRemoveTokenFromUrlParams = (params: Record<string, string>, paramName): string | null => {
  if (paramName in params) {
    const token = params[paramName];
    delete params[paramName];
    pushParamsToHistory(params);
    if (token) {
      return token;
    }
  }
  return null;
};
